/**
 * The hls.js loader for the `peepshow://` scheme (SPEC.md §4).
 *
 * This is the whole trick that makes zero-knowledge HLS work:
 *
 *   peepshow:key              -> the 16-byte content key straight from memory.
 *                                No network request is made, so there is no URL
 *                                for the server to serve and nothing to leak.
 *   peepshow://<v>/m|/v/<blob> -> fetch the blob, AES-GCM decrypt, hand back
 *                                m3u8 text. The server never sees a manifest.
 *   peepshow://<v>/s/<blob>    -> fetch the blob and hand back the bytes
 *                                *still encrypted*. hls.js does the AES-128-CBC
 *                                itself on its native path, which is what makes
 *                                four simultaneous streams affordable.
 */

import type { Loader, LoaderCallbacks, LoaderConfiguration, LoaderContext } from 'hls.js';
import { api } from './api';
import { unseal } from './crypto';

export const KEY_SENTINEL = 'peepshow:key';

export interface ResolverOptions {
  /** Decrypts manifest blobs. */
  metaKey: CryptoKey;
  /** The unwrapped 16-byte AES-128 key for this video. */
  contentKey: Uint8Array;
  /**
   * Reports where each blob came from.
   *
   * Whether playback is served from disk is only knowable here, at the fetch.
   * Anything upstream is inferring it from a segment map, which is a different
   * claim and can be wrong while the cache is working perfectly.
   */
  onSource?(kind: 'manifest' | 'segment', uuid: string, cached: boolean): void;
}

type Resolved = { text: string } | { bytes: Uint8Array };

/**
 * Resolve one `peepshow` URL. Kept separate from the loader class so it can be
 * tested without hls.js, MSE, or a DOM.
 */
export async function resolvePeepshowUrl(
  url: string,
  opts: ResolverOptions,
  signal?: AbortSignal,
): Promise<Resolved> {
  if (url === KEY_SENTINEL || url.replace(/\/+$/, '') === KEY_SENTINEL) {
    // Copy: hls.js may retain or transfer the buffer it is given.
    return { bytes: new Uint8Array(opts.contentKey) };
  }

  const match = /^peepshow:\/\/([^/]+)\/([mvs])\/([^/?#]+)/.exec(url);
  if (!match) throw new Error(`unroutable peepshow URL: ${url}`);
  const [, , kind, blobUuid] = match;

  const raw = await api.getBlob(blobUuid, signal, (cached) =>
    opts.onSource?.(kind === 's' ? 'segment' : 'manifest', blobUuid, cached),
  );
  if (kind === 's') return { bytes: raw }; // still HLS-AES-128; hls.js decrypts.
  return { text: new TextDecoder().decode(await unseal(opts.metaKey, raw)) };
}

/** The stats object hls.js expects a loader to maintain. */
function newStats() {
  return {
    aborted: false,
    loaded: 0,
    retry: 0,
    total: 0,
    chunkCount: 0,
    bwEstimate: 0,
    loading: { start: 0, first: 0, end: 0 },
    parsing: { start: 0, end: 0 },
    buffering: { start: 0, first: 0, end: 0 },
  };
}

function now(): number {
  return typeof performance !== 'undefined' ? performance.now() : 0;
}

/**
 * Build the loader class for one video. hls.js instantiates whatever it is
 * given, so the per-video keys are captured in this closure rather than passed
 * through config — they must never end up somewhere serializable.
 */
export function createPeepshowLoader(opts: ResolverOptions): new () => Loader<LoaderContext> {
  return class PeepshowLoader implements Loader<LoaderContext> {
    context!: LoaderContext;
    stats = newStats();
    private controller: AbortController | null = null;

    load(
      context: LoaderContext,
      _config: LoaderConfiguration,
      callbacks: LoaderCallbacks<LoaderContext>,
    ): void {
      this.context = context;
      this.controller = new AbortController();
      const stats = this.stats;
      stats.loading.start = now();

      resolvePeepshowUrl(context.url, opts, this.controller.signal)
        .then((resolved) => {
          if (stats.aborted) return;
          const wantsBuffer = context.responseType === 'arraybuffer';
          let data: string | ArrayBuffer;
          let length: number;

          if ('text' in resolved) {
            if (wantsBuffer) {
              const encoded = new TextEncoder().encode(resolved.text);
              data = encoded.buffer as ArrayBuffer;
              length = encoded.byteLength;
            } else {
              data = resolved.text;
              length = resolved.text.length;
            }
          } else {
            const bytes = resolved.bytes;
            length = bytes.byteLength;
            data = wantsBuffer
              ? (bytes.buffer.slice(
                  bytes.byteOffset,
                  bytes.byteOffset + bytes.byteLength,
                ) as ArrayBuffer)
              : new TextDecoder().decode(bytes);
          }

          stats.loading.first = stats.loading.end = now();
          stats.loaded = stats.total = length;
          const elapsed = Math.max(1, stats.loading.end - stats.loading.start);
          stats.bwEstimate = (length * 8000) / elapsed;
          callbacks.onSuccess({ url: context.url, data }, stats, context, null);
        })
        .catch((err: unknown) => {
          if (stats.aborted) return;
          stats.loading.end = now();
          const aborted = err instanceof DOMException && err.name === 'AbortError';
          if (aborted) return;
          callbacks.onError(
            { code: 0, text: err instanceof Error ? err.message : String(err) },
            context,
            null,
            stats,
          );
        });
    }

    abort(): void {
      this.stats.aborted = true;
      this.controller?.abort();
    }

    destroy(): void {
      this.abort();
      this.controller = null;
    }
  };
}

/** The master playlist URL handed to `hls.loadSource()`. */
export function masterUrl(videoUuid: string, masterBlob: string): string {
  return `peepshow://${videoUuid}/m/${masterBlob}`;
}
